import { DestroyRef, inject, Injectable } from '@angular/core';
import { GoogleAnalyticsService } from '@lx/shared/google-analytics';
import { filter, skip } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuoteStore } from '../store';
import { GoogleAnalyticsEventEnum } from './google-analytics-event.enum';

/**
 * Service to track quote update events in the Online Quote Tool.
 * Subscribes to quote pricing changes and tracks them via Google Analytics.
 */
@Injectable({
  providedIn: 'root',
})
export class UpdatedQuoteTrackingService {
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly destroyRef = inject(DestroyRef);

  // The action to be tracked when a quote is updated
  private readonly quoteUpdatedAction = 'quote_updated';

  /**
   * Starts tracking quote updates by subscribing to the quote store's price data.
   * Emits an event whenever pricing information changes.
   */
  startTrackingUpdatedQuoteEvent(): void {
    this.quoteStore.pricesSelectionForDataLayer$
      .pipe(
        skip(1), // Skip the first emission (initial load)
        filter((dataLayer) => {
          return Object.values(dataLayer).some((value) => value !== undefined && value !== null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.googleAnalyticsService.trackEvent(GoogleAnalyticsEventEnum.QuoteUpdated, this.quoteUpdatedAction);
      });
  }
}
